// Realtime channel: one EventSource on /api/stream shared by every screen.
// Reconnects with backoff and fresh auth params (Entra tokens in the URL expire).

import { createContext, useContext, useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { STREAM_EVENTS, streamUrl, type StreamEventMap, type StreamEventName } from './api.ts';

export type StreamState = 'connecting' | 'open' | 'down';

export interface StreamStatus {
  state: StreamState;
  lastEventAt: number | null;
  attempts: number;
}

type Handler<K extends StreamEventName> = (data: StreamEventMap[K]) => void;
type AnyHandler = (data: unknown) => void;

export class StreamClient {
  private es: EventSource | null = null;
  private handlers = new Map<StreamEventName, Set<AnyHandler>>();
  private statusSubs = new Set<() => void>();
  private status: StreamStatus = { state: 'connecting', lastEventAt: null, attempts: 0 };
  private timer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  /** Bumped on every start/stop so a connect() still awaiting auth params from an old run gives up. */
  private gen = 0;

  start(): void {
    this.stopped = false;
    this.gen++;
    void this.connect();
    // The server pings every 20 s; if nothing arrives for 45 s, assume the connection is dead.
    this.watchdog = setInterval(() => {
      if (this.status.state === 'open' && this.status.lastEventAt && Date.now() - this.status.lastEventAt > 45_000) {
        this.fail();
      }
    }, 5_000);
  }

  stop(): void {
    this.stopped = true;
    this.gen++;
    if (this.timer) clearTimeout(this.timer);
    if (this.watchdog) clearInterval(this.watchdog);
    this.es?.close();
    this.es = null;
  }

  on<K extends StreamEventName>(name: K, h: Handler<K>): () => void {
    let set = this.handlers.get(name);
    if (!set) {
      set = new Set();
      this.handlers.set(name, set);
    }
    const anyH = h as AnyHandler;
    set.add(anyH);
    return () => set.delete(anyH);
  }

  getStatus = (): StreamStatus => this.status;

  subscribeStatus = (cb: () => void): (() => void) => {
    this.statusSubs.add(cb);
    return () => this.statusSubs.delete(cb);
  };

  private setStatus(patch: Partial<StreamStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const cb of this.statusSubs) cb();
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    const gen = this.gen;
    this.setStatus({ state: 'connecting' });
    let url: string;
    try {
      url = await streamUrl();
    } catch {
      if (gen === this.gen) this.fail();
      return;
    }
    if (this.stopped || gen !== this.gen) return;
    this.es?.close();
    const es = new EventSource(url);
    this.es = es;
    es.onopen = () => this.setStatus({ state: 'open', attempts: 0, lastEventAt: Date.now() });
    es.onerror = () => {
      if (this.es === es) this.fail();
    };
    for (const name of STREAM_EVENTS) {
      es.addEventListener(name, (ev: MessageEvent<string>) => this.dispatch(name, ev.data));
    }
  }

  private dispatch(name: StreamEventName, raw: string): void {
    this.setStatus({ state: 'open', lastEventAt: Date.now() });
    let data: unknown = {};
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        return;
      }
    }
    const set = this.handlers.get(name);
    if (!set) return;
    for (const h of set) {
      try {
        h(data);
      } catch (e) {
        console.error(`stream handler for "${name}" failed`, e);
      }
    }
  }

  private fail(): void {
    this.es?.close();
    this.es = null;
    if (this.stopped) return;
    const attempts = this.status.attempts + 1;
    this.setStatus({ state: 'down', attempts });
    const delay = Math.min(15_000, 1_000 * 2 ** Math.min(attempts - 1, 4));
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.connect(), delay);
  }
}

const StreamContext = createContext<StreamClient | null>(null);

export function StreamProvider({ children }: { children: ReactNode }) {
  const ref = useRef<StreamClient | null>(null);
  if (!ref.current) ref.current = new StreamClient();
  const client = ref.current;
  useEffect(() => {
    client.start();
    return () => client.stop();
  }, [client]);
  return <StreamContext.Provider value={client}>{children}</StreamContext.Provider>;
}

function useClient(): StreamClient {
  const c = useContext(StreamContext);
  if (!c) throw new Error('StreamProvider missing');
  return c;
}

/** Subscribes to one SSE event type for the lifetime of the component. */
export function useStreamEvent<K extends StreamEventName>(name: K, handler: Handler<K>): void {
  const client = useClient();
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => client.on(name, (d) => ref.current(d)), [client, name]);
}

/** Calls `refresh` (debounced) when any of the named events arrives. */
export function useRefreshOn(names: readonly StreamEventName[], refresh: () => void, delayMs = 400): void {
  const client = useClient();
  const ref = useRef(refresh);
  useEffect(() => {
    ref.current = refresh;
  });
  const key = names.join(',');
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => ref.current(), delayMs);
    };
    const offs = key.split(',').map((n) => client.on(n as StreamEventName, fire));
    return () => {
      offs.forEach((off) => off());
      if (t) clearTimeout(t);
    };
  }, [client, key, delayMs]);
}

export function useStreamStatus(): StreamStatus {
  const client = useClient();
  return useSyncExternalStore(client.subscribeStatus, client.getStatus, client.getStatus);
}
